import React from "react";
import { AccountType, TesteResultType } from "../../../ImportScreen/types";
import { QuizType } from "app/types";

type TestResult = {
  quizAnswer: TesteResultType;
  account: AccountType;
  quiz: QuizType;
};

interface Recomendacao {
  texto: string;
}

type ExameProps = {
  dataGeracao: string;
  testResult: TestResult;
  mostrarApenasItens?: number;
  recomendacoes: Recomendacao[];
  diagnostico: string;
  codigoVerificacao?: string;
  translate: (key: string) => string;
  // adicionais para o HTML por exemplo um <div> com a assinatura do responsável como se foce mais filho <div class="responsible-signature"><Chart/></div>
  children?: React.ReactNode;
};

/**
 * Separa um string em varias string[] seprandas por ;
 * @param value string a ser separada
 * @returns Array de strings separadas
 */
export const parseStringToArray = (value: string): string[] => {
  if (!value) return [];
  return value
    .split(";")
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

/**
 * Renderiza um relatório de exame olfativo em HTML
 * @param props Dados do exame a serem renderizados
 * @returns String HTML do relatório pronto para ser renderizado
 */
export const renderExameHTML = (props: ExameProps): string => {
  const { quizAnswer, account, quiz } = props.testResult;
  const { translate } = props;
  // Calcular a porcentagem de precisão
  const total = quizAnswer.quizAnswer?.length || 0;
  const acertos =
    quizAnswer.quizAnswer?.filter(a => a.answer === a.rightAnswer).length || 0;
  const precisao = total > 0 ? (acertos / total) * 100 : 0;

  // Formatar a tabela de questões
  const linhasTabela = quizAnswer.quizAnswer
    ?.slice(0, props.mostrarApenasItens || quizAnswer.quizAnswer.length)
    .map((questao, index) => {
      const ehCerta = questao.answer === questao.rightAnswer;
      const iconClass = ehCerta ? "icon correct" : "icon";
      const resultadoClass = ehCerta
        ? translate("screenApp_results_right")
        : translate("screenApp_results_wrong");
      const resultadoTexto = ehCerta
        ? translate("screenApp_results_right")
        : translate("screenApp_results_wrong");

      // Calcular o percentual de tempo (usando o tempo médio como base)
      const tempoTotalSegundos =
        (new Date(questao.endDateTime).getTime() -
          new Date(questao.initDate).getTime()) /
        1000;
      const tempoPercentual = Math.min((tempoTotalSegundos / 30) * 100, 100); // Assumindo 30 segundos como tempo máximo

      return `
        <tr>
            <td>${index + 1}</td>
            <td>
                <div class="options-container">
                ${parseStringToArray(questao?.otherAnswers || "")
                  .map(
                    (option, i) =>
                      `<span class="options-chip">${option}</span>`,
                  )
                  .join("")}
                </div>
            </td>
            <td>${questao.answer}</td>
            <td>${questao.rightAnswer}</td>
            <td><span class="${iconClass}">${
        ehCerta ? "✓" : "✕"
      }</span><span class="${resultadoClass}">${resultadoTexto}</span></td>
      <td>${questao.shots}</td>
            <td>
                <div class="time-bar">
                    <div class="time-fill" style="width: ${tempoPercentual}%;"></div>
                </div>
                ${Math.round(tempoTotalSegundos)}s
            </td>
        </tr>
      `;
    })
    .join("");

  const tempoTotal = quizAnswer.quizAnswer?.reduce((total, q) => {
    return (
      total +
      (new Date(q.endDateTime).getTime() - new Date(q.initDate).getTime()) /
        1000
    );
  }, 0);

  const tempoTotalFormatado = tempoTotal
    ? `${Math.floor(tempoTotal / 60)}m ${Math.round(tempoTotal % 60)}s`
    : "0s";

  const renderRecomendacoes = () => {
    return `<div class="card">
                <h2 class="card-title">${translate(
                  "labels_diagnosis_recommendations",
                )}</h2>

                <p style="margin-bottom: 20px;">${props.diagnostico}</p>

                <div style="display: flex; gap: 15px; margin-bottom: 20px;">
                    <span class="badge blue">${translate(
                      "labels_preliminary_report",
                    )}</span>
                    <span class="badge red">${translate(
                      "labels_deficit",
                    )}</span>
                </div>

                <p style="margin-bottom: 10px;"><strong>${translate(
                  "labels_recommendations",
                )}:</strong></p>
                <ul style="list-style-type: none; padding-left: 20px;">
                    ${props.recomendacoes
                      .map(
                        rec =>
                          `<li style="margin-bottom: 8px;">• ${rec.texto}</li>`,
                      )
                      .join("")}
                </ul>
            </div>`;
  };

  // Construir o HTML completo
  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>NOAR Health - ${translate("labels_result_scan")}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

        :root {
            --primary-color: #4d6feb;
            --secondary-color: #6c63ff;
            --accent-color: #8a7fff;
            --success-color: #4CAF50;
            --error-color: #F44336;
            --background-color: #f8fbff;
            --card-color: #ffffff;
            --text-color: #333;
            --text-light: #666;
            --border-radius: 12px;
            --box-shadow: 0 8px 20px rgba(77, 111, 235, 0.15);
        }

        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-family: 'Poppins', sans-serif;
        }

        body {
            background-color: var(--background-color);
            color: var(--text-color);
            line-height: 1.6;
            width: 100%;
            max-width: 100%;
            margin: 0;
            padding: 0;
        }

        .page-container {
            width: 21cm;
            margin: 0 auto;
            background: var(--card-color);
            box-shadow: var(--box-shadow);
            position: relative;
            overflow: hidden;
        }

        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 25px 40px;
            border-bottom: 1px solid #eaeaea;
        }

        .logo-title-container {
            display: flex;
            align-items: center;
            gap: 20px;
        }

        .logo {
            width: 100px;
            height: auto;
        }

        .document-title {
            font-size: 24px;
            font-weight: 600;
            color: var(--primary-color);
            margin: 0;
        }

        .document-meta {
            color: var(--text-light);
            font-size: 14px;
            text-align: right;
        }

        .content {
            padding: 30px 20px 30px;
        }

        .card {
            background: var(--card-color);
            border-radius: var(--border-radius);
            padding: 20px;
            margin-bottom: 25px;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
            border: 1px solid #eaeaea;
        }

        .card-title {
            font-size: 18px;
            font-weight: 600;
            color: var(--primary-color);
            margin-bottom: 15px;
            display: flex;
            align-items: center;
        }

        .card-title::before {
            content: "";
            display: inline-block;
            width: 12px;
            height: 12px;
            background: var(--primary-color);
            border-radius: 50%;
            margin-right: 10px;
        }

        .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 25px;
        }

        .info-group {
            margin-bottom: 12px;
            display: flex;
        }

        .info-label {
            font-weight: 500;
            color: var(--text-light);
            min-width: 140px;
        }

        .info-value {
            font-weight: 500;
        }

        .result-summary {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: #f5f7ff;
            padding: 20px;
            border-radius: var(--border-radius);
            margin: 20px 0;
        }

        .result-metric {
            text-align: center;
        }

        .result-number {
            font-size: 32px;
            font-weight: 700;
            color: var(--primary-color);
            display: block;
        }

        .result-label {
            color: var(--text-light);
            font-size: 14px;
        }

        .accuracy-chart {
            position: relative;
            width: 120px;
            height: 120px;
            border-radius: 50%;
            background: conic-gradient(
                var(--error-color) 0% ${100 - precisao}%,
                var(--success-color) ${100 - precisao}% 100%
            );
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .chart-inner {
            width: 90px;
            height: 90px;
            background: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
        }

        .chart-percentage {
            font-size: 24px;
            font-weight: 700;
            color: var(--primary-color);
        }

        .chart-label {
            font-size: 12px;
            color: var(--text-light);
        }

        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            border-radius: var(--border-radius);
            overflow: hidden;
            box-shadow: 0 0 0 1px #eaeaea;
        }

        th {
            background: #f0f3ff;
            color: var(--primary-color);
            font-weight: 600;
            text-align: left;
            padding: 15px;
            font-size: 14px;
        }

        td {
            padding: 12px 15px;
            border-bottom: 1px solid #eaeaea;
            font-size: 14px;
        }

        tr:last-child td {
            border-bottom: none;
        }

        tr:nth-child(even) {
            background-color: #fafbff;
        }

        .errada {
            color: var(--error-color);
            font-weight: 600;
            position: relative;
        }

        .certa {
            color: var(--success-color);
            font-weight: 600;
            position: relative;
        }

        .icon {
            display: inline-flex;
            width: 20px;
            height: 20px;
            background: var(--error-color);
            color: white;
            border-radius: 50%;
            align-items: center;
            justify-content: center;
            margin-right: 5px;
            font-size: 12px;
            font-weight: bold;
        }

        .icon.correct {
            background: var(--success-color);
        }

        .footerExame {
            text-align: center;
            padding: 25px 0;
            border-top: 1px solid #eaeaea;
            margin-top: 30px;
            color: var(--text-light);
            font-size: 12px;
            background: #fafbff;
        }

        .stamp {
            display: flex;
            justify-content: flex-end;
            margin: 40px 0;
        }

        .stamp-inner {
            border: 2px solid var(--primary-color);
            border-radius: 8px;
            padding: 15px 25px;
            color: var(--primary-color);
            font-weight: 500;
            position: relative;
        }

        .stamp-inner::after {
            content: "NOAR";
            position: absolute;
            font-size: 60px;
            color: rgba(77, 111, 235, 0.07);
            font-weight: 700;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 0;
        }

        .responsible-section {
            font-size: 13px;
            color: var(--text-light);
            margin: 20px 0;
            line-height: 1.8;
        }

        .responsible-signature {
            display: inline-block;
            border-top: 1px solid #aaa;
            padding-top: 5px;
            margin-top: 20px;
            font-style: italic;
        }

        .badge {
            display: inline-block;
            padding: 3px 10px;
            border-radius: 15px;
            font-size: 12px;
            font-weight: 500;
            margin-right: 5px;
        }

        .badge.red {
            background: rgba(244, 67, 54, 0.1);
            color: var(--error-color);
        }

        .badge.green {
            background: rgba(76, 175, 80, 0.1);
            color: var(--success-color);
        }

        .badge.blue {
            background: rgba(77, 111, 235, 0.1);
            color: var(--primary-color);
        }

        .time-bar {
            display: inline-block;
            width: 50px;
            height: 5px;
            background: #eee;
            border-radius: 2px;
            position: relative;
            margin-right: 5px;
        }

        .time-fill {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            background: var(--accent-color);
            border-radius: 2px;
        }

        .options-chip {
            background: #f0f3ff;
            border-radius: 4px;
            padding: 2px 6px;
            margin-right: 5px;
            font-size: 12px;
            color: var(--primary-color);
            border: 1px solid rgba(77, 111, 235, 0.3);
            white-space: nowrap;
        }

        .options-container {
            display: flex;
            gap: 5px;
            flex-wrap: wrap;
        }

        .timestamp {
            display: block;
            font-size: 11px;
            color: var(--text-light);
            margin-top: 5px;
            text-align: right;
        }

        .table-scroll {
            overflow-x: auto;
            margin-bottom: 20px;
        }

        @media print {
            body {
                background-color: white;
            }

            .page-container {
                box-shadow: none;
            }

            table {
                page-break-inside: auto;
            }

            tr {
                page-break-inside: avoid;
                page-break-after: auto;
            }
        }
    </style>
</head>
<body>
    <div class="page-container">
        <div class="header">
            <div class="logo-title-container">
                <img src="https://app.noar.health/static/media/logo.9e58e41047c0a7bd3611.png" alt="NOAR Health Logo" class="logo">
                <h1 class="document-title">${translate(
                  "labels_report_assessment",
                )}</h1>
            </div>
            <div class="document-meta">
                <p>${translate("labels_document_generated_at")}: ${
    props.dataGeracao
  }</p>
            </div>
        </div>

        <div class="content">
            <div class="card">
                <h2 class="card-title">${translate("labels_patient_info")}</h2>
                <div class="grid-2">
                    <div>
                        <div class="info-group">
                            <span class="info-label">${translate(
                              "screenApp_results_patient",
                            )}:</span>
                            <span class="info-value">${account.name}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">${translate(
                              "labels_registration",
                            )}:</span>
                            <span class="info-value">${account.accountId}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">${translate(
                              "screens_user_labels_birthday",
                            )}:</span>
                            <span class="info-value">${new Date(
                              account.birthDate,
                            ).toLocaleDateString()}</span>
                        </div>
                    </div>
                    <div>
                        <div class="info-group">
                            <span class="info-label">${translate(
                              "date_scan",
                            )}:</span>
                            <span class="info-value">${new Date(
                              quizAnswer.quizAnswer?.[0]?.initDate,
                            ).toLocaleString()}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">${translate(
                              "labels_id_scam",
                            )}:</span>
                            <span class="info-value">${
                              quizAnswer.testResultId
                            }</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">${translate(
                              "labels_realized_by",
                            )}:</span>
                            <span class="info-value">${quizAnswer
                              .quizAnswer?.[0]?.doctor ||
                              translate("labels_not_informed")}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <h2 class="card-title">${translate("labels_results_for")} ${
    props.testResult.quiz.name
  }</h2>
                <div class="result-summary">
                    <div class="result-metric">
                        <span class="result-number">${acertos}/${total}</span>
                        <span class="result-label">${translate(
                          "screenApp_results_details_totalHits",
                        )}</span>
                    </div>
                    <div class="result-metric">
                        <span class="result-number">${tempoTotalFormatado}</span>
                        <span class="result-label">${translate(
                          "screenApp_results_time2",
                        )}</span>
                    </div>
                    <div class="accuracy-chart">
                        <div class="chart-inner">
                            <span class="chart-percentage">${precisao.toFixed(
                              1,
                            )}%</span>
                            <span class="chart-label">${translate(
                              "labels_accuracy",
                            )}</span>
                        </div>
                    </div>
                </div>

                <div class="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>${translate(
                                  "screens_quiz_labels_question",
                                )}</th>
                                <th>${translate(
                                  "screenApp_results_options",
                                )}</th>
                                <th>${translate("labels_answers_pacient")}</th>
                                <th>${translate("labels_answers_right")}</th>
                                <th>${translate(
                                  "screenApp_results_result",
                                )}</th>
                                <th>${translate("Disparos")}</th>
                                <th>${translate("screenApp_results_time")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${linhasTabela}
                        </tbody>
                    </table>
                </div>

                ${
                  props.mostrarApenasItens &&
                  props.mostrarApenasItens <
                    (quizAnswer.quizAnswer?.length || 0)
                    ? `
                <div class="timestamp">
                    * ${translate("screens_exame_table_presents").replace(
                      "{{showItems}}",
                      props.mostrarApenasItens.toString(),
                    )}
                </div>
                `
                    : ""
                }

                <div class="stamp">
                    <div class="stamp-inner">
                        <p>${translate(
                          "labels_official_document",
                        )} • NOAR Health</p>
                        <p>${translate("labels_assessment")} • ${translate(
    "labels_diagnosis_report",
  )}</p>
                    </div>
                </div>

                <div class="responsible-section">
                    <p><strong>${translate(
                      "labels_responsible",
                    )}:</strong> Dr. Marcio Nakanishi CRM-DF 13.081</p>
                    <p><strong>${translate(
                      "labels_realization",
                    )}:</strong> SGAS 610 BL I Sla S-2, Brasília - DF, 70200-700</p>
                </div>
            </div>

            ${
              props.children
                ? `
              <div class="card">
              ${props.children}
              </div>
              `
                : ""
            }


        </div>

        <div class="footerExame">
            <p>© ${new Date().getFullYear()} NOAR Health. Todos os direitos reservados.</p>
            <p>${translate("screens_perception_document")}</p>
            <p>${translate("labels_check_code")}: ${props.codigoVerificacao ||
    "NOAR-" +
      Math.random()
        .toString(36)
        .substring(2, 11)
        .toUpperCase()} | ${translate("labels_generated")}: ${
    props.dataGeracao
  }</p>
        </div>
    </div>
</body>
</html>
  `;
};
